import { Fragment, useMemo } from 'react';
import { CALENDAR_MONTHS_AHEAD } from '@etown/shared';
import { Modal } from '../../components/Modal';
import './objects.css';

type DayCell = { day: number; isToday: boolean } | null;

interface WeekRow {
  kw: number;
  days: DayCell[];
}

interface MonthGrid {
  key: string;
  label: string;
  weeks: WeekRow[];
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** ISO-8601-Kalenderwoche eines Datums (Woche mit dem ersten Donnerstag). */
function isoWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Donnerstag dieser Woche bestimmt das Jahr; ISO-Tag Mo=1..So=7
  const dayNr = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const firstDayNr = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNr + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
}

/** Baut das Wochenraster eines Monats (Montag als Wochenstart, mit KW). */
function buildMonth(year: number, month: number, today: Date): MonthGrid {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // getDay(): So=0..Sa=6 -> auf Mo=0..So=6 verschieben
  const leading = (first.getDay() + 6) % 7;
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const cells: DayCell[] = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ day, isToday: isCurrentMonth && today.getDate() === day });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: WeekRow[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    const days = cells.slice(i, i + 7);
    // KW aus dem ersten realen Tag der Zeile (Montag, falls vorhanden)
    const anchor = days.find((c) => c !== null);
    const kw = anchor ? isoWeek(new Date(year, month, anchor.day)) : 0;
    weeks.push({ kw, days });
  }

  const label = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(first);
  return { key: `${year}-${month}`, label, weeks };
}

/**
 * Klassischer Wandkalender: aktueller Monat + CALENDAR_MONTHS_AHEAD Folgemonate.
 * Rein clientseitig aus dem lokalen Datum — keine Persistenz noetig.
 */
export function CalendarModal({ onClose }: { onClose: () => void }) {
  const months = useMemo(() => {
    const today = new Date();
    const result: MonthGrid[] = [];
    for (let offset = 0; offset <= CALENDAR_MONTHS_AHEAD; offset += 1) {
      const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
      result.push(buildMonth(d.getFullYear(), d.getMonth(), today));
    }
    return result;
  }, []);

  return (
    <Modal title="Kalender" onClose={onClose} wide>
      <div className="calendar">
        {months.map((month) => (
          <div key={month.key} className="calendar__month">
            <h3 className="calendar__title">{month.label}</h3>
            <div className="calendar__grid">
              <span className="calendar__weekday calendar__kw-head">KW</span>
              {WEEKDAYS.map((wd) => (
                <span key={wd} className="calendar__weekday">
                  {wd}
                </span>
              ))}
              {month.weeks.map((week) => (
                <Fragment key={week.kw}>
                  <span className="calendar__kw">{week.kw}</span>
                  {week.days.map((cell, i) => (
                    <span
                      key={i}
                      className={`calendar__day ${cell?.isToday ? 'calendar__day--today' : ''} ${cell ? '' : 'calendar__day--empty'}`}
                    >
                      {cell?.day ?? ''}
                    </span>
                  ))}
                </Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
